
import React from 'react';
import { Compass, Users, Briefcase, Heart, Home, Zap, GraduationCap } from 'lucide-react';
import { Service, Language } from '../types';
import { translations } from '../translations';

export const SERVICES: Service[] = [
  {
    id: 'natal',
    title: 'Natal Chart Analysis',
    description: 'A deep dive into your unique cosmic blueprint at the moment of your birth. Discover your life purpose, hidden talents, and core challenges.',
    longDescription: 'The Natal Chart is the fundamental map of your soul. In this session, we analyze the exact positions of the planets at the moment of your birth. This reading provides profound clarity on your personality structure, karmic baggage, and the evolutionary path your soul has chosen for this lifetime.',
    price: 300,
    duration: '60 mins',
    icon: 'Compass',
    image: 'https://images.unsplash.com/photo-1532968961962-8a0cb3a2d4f5?auto=format&fit=crop&q=80&w=2000'
  },
  {
    id: 'teaching',
    title: 'Live Astrology Mastery',
    description: 'A private 180-minute teaching session and free-for-all Q&A on how to interpret the celestial heavens.',
    longDescription: 'This intensive session is for serious students. We move beyond simple readings and into the mechanics of astrology. Bring any questions about house systems, transits, or aspect patterns. The session is recorded and added to your private vault.',
    price: 950,
    duration: '180 mins',
    icon: 'GraduationCap',
    image: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?auto=format&fit=crop&q=80&w=2000'
  },
  {
    id: 'synastry',
    title: 'Relationship Synastry',
    description: 'Explore the energetic dynamic between two people. Perfect for romantic partners, family, or business associates looking to align.',
    longDescription: 'Synastry is the art of relationship astrology. By overlaying two birth charts, we can see exactly where energies harmonize and where they clash.',
    price: 440,
    duration: '90 mins',
    icon: 'Users',
    image: 'https://images.unsplash.com/photo-1518133910546-b6c2fb7d79e3?auto=format&fit=crop&q=80&w=2000'
  },
  {
    id: 'career',
    title: 'Career & Wealth Path',
    description: 'Find your vocational calling. We focus on Midheaven and 2nd/6th/10th house analysis to map out your professional success.',
    longDescription: 'Your vocational destiny is written in the stars. In this focused reading, we look specifically at your Midheaven (MC), your houses of finance, and your innate skills.',
    price: 260,
    duration: '60 mins',
    icon: 'Briefcase',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=2000'
  },
  {
    id: 'love',
    title: 'Love & Heart Alignment',
    description: 'Delve into your romantic potential. We analyze your Venus placement, relationship patterns, and the timing of new love.',
    longDescription: 'This session focuses exclusively on the heart. We explore your Venus and Mars placements to understand your attraction patterns and what you truly need to feel loved.',
    price: 280,
    duration: '60 mins',
    icon: 'Heart',
    image: 'https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?auto=format&fit=crop&q=80&w=2000'
  },
  {
    id: 'family',
    title: 'Family Dynamics',
    description: 'Understand the karmic ties and ancestral patterns within your family. Great for healing generational wounds.',
    longDescription: 'Family dynamics are often rooted in ancestral patterns that go back generations. This reading explores the 4th house and planetary links between family members.',
    price: 320,
    duration: '75 mins',
    icon: 'Home',
    image: 'https://images.unsplash.com/photo-1511895426328-dc8714191300?auto=format&fit=crop&q=80&w=2000'
  }
];

const IconMap: Record<string, any> = {
  Compass,
  Users,
  Briefcase,
  Heart,
  Home,
  Zap,
  GraduationCap
};

interface ServicesProps {
  onSelectService: (id: string) => void;
  lang?: Language;
}

const Services: React.FC<ServicesProps> = ({ onSelectService, lang = 'en' }) => {
  const t = (translations[lang] || translations.en).services;

  return (
    <section id="services" className="py-24 bg-slate-950/40 px-4 backdrop-blur-[2px]">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-amber-400 text-sm tracking-[0.2em] uppercase mb-2 drop-shadow-[0_0_8px_rgba(245,158,11,0.3)]">{t.tag}</h2>
          <h3 id="services-title" className="text-4xl md:text-5xl font-serif text-slate-50">{t.title}</h3>
          <div className="w-24 h-1 bg-amber-500 mx-auto mt-6 shadow-[0_0_10px_rgba(245,158,11,0.5)]" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map((service) => {
            const Icon = IconMap[service.icon];
            return (
              <button
                key={service.id}
                onClick={() => onSelectService(service.id)}
                className={`group relative bg-slate-900/60 backdrop-blur-md border border-indigo-900/50 p-8 hover:border-amber-500/50 transition-all duration-500 hover:-translate-y-2 text-left w-full overflow-hidden ${service.id === 'teaching' ? 'ring-2 ring-amber-500/20' : ''}`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-700" />
                <div className="mb-6 inline-block p-3 bg-indigo-950/80 text-amber-400 group-hover:bg-amber-400 group-hover:text-slate-900 transition-colors shadow-lg relative z-10">
                  <Icon size={28} />
                </div>
                {service.id === 'teaching' && <span className="absolute top-8 right-8 text-[8px] uppercase tracking-widest font-bold bg-amber-500 text-slate-950 px-2 py-0.5">Premium</span>}
                <h4 className="text-xl font-serif text-slate-50 mb-4 relative z-10">{service.title}</h4>
                <p className="text-indigo-200/70 text-sm leading-relaxed mb-6 h-24 overflow-hidden relative z-10">
                  {service.description}
                </p>
                <div className="flex justify-between items-end border-t border-indigo-900/30 pt-4 relative z-10">
                  <div>
                    <span className="text-xs text-indigo-400 uppercase tracking-widest font-bold">{t.duration}</span>
                    <p className="text-indigo-100 font-medium">{service.duration}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-amber-500 uppercase tracking-widest font-bold group-hover:text-amber-400">{t.learnMore} →</span>
                    <p className="text-amber-400 font-bold text-lg">${service.price}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
